import React from 'react';
import { TileButtonProps } from '@printdeal/ui-library';

import { SanitizedLineItem, SanitizedOrder } from '../../../../../../types/order/Order';

import canDeliverFilesHelper from './CanDeliverFilesHelper';

import canRequestFileChangeHelper, { ReasonsDisabled as ReasonsChangeFile } from '../../../order-detail-page/LineItemActions/RequestFileChange/CanRequestFileChangeHelper';

import canRequestFileDesignHelper, { ReasonsDisabled as ReasonsDesignFile } from '../../../order-detail-page/LineItemActions/RequestFileDesign/helpers/CanRequestFileDesignHelper';

import useUploadFiles, { ReasonsDisabled as ReasonsUploadFiles } from '../UploadFiles/useUploadFiles';
import OpenDeliveryToolButton from './OpenDeliveryToolButton';

import RequestFileChangeThumbButton from '../../../order-detail-page/LineItemActions/RequestFileChange/RequestFileChangeThumbButton';
import RequestedFileChangeThumbButton from '../../../order-detail-page/LineItemActions/RequestFileChange/RequestedFileChangeThumbButton';
import RequestFileDesignThumbButton from '../../../order-detail-page/LineItemActions/RequestFileDesign/RequestFileDesignThumbButton';
import RequestedFileDesignThumbButton from '../../../order-detail-page/LineItemActions/RequestFileDesign/RequestedFileDesignThumbButton';
import OpenAlternativeUploadThumbButton from '../AlternativeUpload/OpenAlternativeUploadThumbButton';
import UploadFilesThumbButton from '../UploadFiles/UploadFilesThumbButton';
import ChooseDeliveryToolThumbButton from './ChooseDeliveryTool/ChooseDeliveryToolThumbButton';
import { DispatchCategoriesAccount } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import DeliverFilesSpinnerThumbButton from './Spinner/DeliverFilesSpinnerThumbButton';

interface DeliverFilesProps {
  order: SanitizedOrder;
  lineItem: SanitizedLineItem;
  dispatchEventCategory?: DispatchCategoriesAccount;
  thumbSize?: TileButtonProps['size'];
  thumbClasses?: string;
}

const DeliverFiles = ({
  order,
  lineItem,
  dispatchEventCategory = 'order_detail_interaction',
  thumbSize = 'lg',
  thumbClasses = '',
}: DeliverFilesProps) => {
  const canDeliverFiles = canDeliverFilesHelper(lineItem);
  const canRequestFileChange = canRequestFileChangeHelper(lineItem, order);
  const canRequestFileDesign = canRequestFileDesignHelper(lineItem, order);
  const canUploadFile = useUploadFiles(lineItem, order);

  if (!canDeliverFiles.enabled) {
    // No files are needed (cancelled, unprinted product, files already delivered).
    return null;
  }

  if (canRequestFileChange.hasService) {
    if (!canRequestFileChange.enabled
      && canRequestFileChange.reasonDisabled === ReasonsChangeFile.REQUEST_DETAILS_SUBMITTED) {
      return <RequestedFileChangeThumbButton thumbSize={thumbSize} thumbClasses={thumbClasses} />;
    }

    if (canRequestFileChange.enabled) {
      return (
        <RequestFileChangeThumbButton
          lineItem={lineItem}
          dispatchEventCategory={dispatchEventCategory}
          thumbSize={thumbSize}
          thumbClasses={thumbClasses}
        />
      );
    }

    return null;
  }

  if (canRequestFileDesign.hasService) {
    if (!canRequestFileDesign.enabled
      && canRequestFileDesign.reasonDisabled === ReasonsDesignFile.REQUEST_DETAILS_SUBMITTED) {
      return <RequestedFileDesignThumbButton thumbSize={thumbSize} thumbClasses={thumbClasses} />;
    }

    if (canRequestFileDesign.enabled) {
      return (
        <RequestFileDesignThumbButton
          lineItem={lineItem}
          dispatchEventCategory={dispatchEventCategory}
          thumbSize={thumbSize}
          thumbClasses={thumbClasses}
        />
      );
    }

    return null;
  }

  if (canUploadFile?.reason === ReasonsUploadFiles.LOADING_DELIVERY_TOOLS) {
    return <DeliverFilesSpinnerThumbButton thumbSize={thumbSize} thumbClasses={thumbClasses} />;
  }

  if (canUploadFile?.deliveryMethods
    && canUploadFile.deliveryMethods.length > 1
    && canUploadFile?.deliveryToolSettings) {
    return (
      <ChooseDeliveryToolThumbButton
        lineItem={lineItem}
        deliveryMethods={canUploadFile.deliveryMethods}
        deliveryToolSettings={canUploadFile.deliveryToolSettings}
        dispatchEventCategory={dispatchEventCategory}
        thumbSize={thumbSize}
        thumbClasses={thumbClasses}
      />
    );
  }

  if (canUploadFile?.deliveryMethods
    && canUploadFile.deliveryMethods.length === 1
    && canUploadFile.deliveryMethods[0] !== 'alternative-upload'
    && canUploadFile?.deliveryToolSettings) {
    return (
      <OpenDeliveryToolButton
        lineItem={lineItem}
        orderId={order.id}
        deliveryToolSettings={canUploadFile.deliveryToolSettings}
      >
        <UploadFilesThumbButton
          dispatchEventCategory={dispatchEventCategory}
          thumbSize={thumbSize}
          thumbClasses={thumbClasses}
        />
      </OpenDeliveryToolButton>
    );
  }

  return (
    <OpenAlternativeUploadThumbButton
      lineItemId={lineItem.id}
      orderId={order.id}
      dispatchEventCategory={dispatchEventCategory}
      thumbSize={thumbSize}
      thumbClasses={thumbClasses}
    />
  );
};

export default DeliverFiles;
